/**
 * View Component: PostCard
 * Single content card in the grid, with A/B version toggle.
 *
 * Props:
 *   post: { id, versions: {A, B}, book, coverUrl, date, seq, favorite, hotTopic, ... }
 *   onToggleFav: (id) => void
 *   onClick: (id) => void
 *   selectionMode: boolean      是否处于多选态(改进清单 #8)
 *   selected: boolean           本卡片是否被选中
 *   onToggleSelect: (id, next) => void
 */
// T-Cron-2026-10-24 02:00 早间:长按多选 hook + 触感反馈收口。
// 直接 import(不走 window)—— 避免依赖 main.jsx 的挂载顺序,
// 也不受 esbuild.jsxInject 影响(这两个都是 .js 文件,自己显式 import React)。
import useLongPress from '../harness/useLongPress.js';
import { buzz } from '../harness/haptics.js';
import CoverDownload from '../harness/coverDownload.js';
// T-Cron-2026-11-01 14:00 — 修「版本状态不同步」(P1)。
// 版本解析口径必须与 harness/postText.js 的 resolveVersionKey **完全一致**
// (批量复制走的就是它) —— 否则「单篇复制」和「批量复制」会挑到不同的版本。
import { resolveVersionKey } from '../harness/postText.js';

function HeartIcon({ filled }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
    </svg>
  );
}

function PostCard({ post, onToggleFav, onClick, preferredVersion, selectionMode = false, selected = false, onToggleSelect }) {
  // 🔴 T-Cron-2026-11-01 20:00 晚间 — 修「用 effect 同步 state」的陈旧帧模式。
  //
  // 【为什么 PostCard 也必须改(哪怕它现在还没白屏)】
  //   14:00 轮把它从 useState('A') + effect 改成了
  //       useState(...) + effect(() => setActiveVersion(resolveVersionKey(...)))
  //   那个 effect 有和 PostDetail **完全相同**的缺陷:
  //     effect 晚于 render 一帧 ⇒ 「外部状态变了」的那一帧仍读到旧值。
  //   PostCard 恰好没炸,只因为它的初值是 'A' 而不是 '',
  //   而 'A' 恰好在**大多数**选题上有效
  //   ⇒ 这是一条「靠数据运气」的隐形地雷:
  //     哪天真出现一篇只有 B/C 版的选题(没有 A 版),
  //     resolveVersionKey 会返回 B/C,但**第一帧**仍在用 'A'
  //     ⇒ versionData 回退扁平 post ⇒ versionData.title undefined
  //     ⇒ 同样的白屏。
  //   ⇒ 与其等它踩雷,不如和 PostDetail 用**同一套** render 期推导。
  //
  // 【改法:React 官方「adjusting state when props change」模式】
  //   · userVersion  —— 用户在卡片上手动切到的版本(可能为 null)
  //   · activeVersion —— 本帧真正用的版本 = userVersion ?? resolveVersionKey(...)
  //   换选题 / 改全局筛选时,在 render 期把 userVersion 重置为 null。
  //   resolveVersionKey 是纯函数 ⇒ render 期调用安全 ⇒ 没有陈旧帧。
  //
  // ⚠️ 依赖用 post.id(不是 post):
  //   收藏会让 store 重建 post 对象(见 useContentStore.setFavorites)
  //   ⇒ 若依赖 post,收藏一下就会把用户手动切到的版本打回默认。
  const [userVersion, setUserVersion] = React.useState(null);
  const [lastPostId, setLastPostId] = React.useState(post ? post.id : null);
  const [lastPreferred, setLastPreferred] = React.useState(preferredVersion);

  // render 期调整:换了选题 / 改了全局版本筛选 ⇒ 丢弃用户手动选择。
  if ((post && post.id) !== lastPostId || preferredVersion !== lastPreferred) {
    setLastPostId(post ? post.id : null);
    setLastPreferred(preferredVersion);
    setUserVersion(null);
  }

  // 本帧真正使用的版本(纯函数推导 ⇒ 无陈旧帧)。
  const resolvedVersion = userVersion || resolveVersionKey(post, preferredVersion);
  const activeVersion =
    (post && post.versions && post.versions[resolvedVersion])
      ? resolvedVersion
      : resolveVersionKey(post, preferredVersion);

  // T-Cron-2026-10-24 02:00 早间 (round-1-of-day) — 改进清单 #8「长按多选」。
  //
  // 卡片进入「多选态」后的语义:
  //   · 整卡是一个 toggle(而不是打开详情)—— 多选时点击 = 选中/取消;
  //   · 卡片右上角出现一个显式的选择勾(覆盖收藏按钮位置),
  //     让"当前是不是选中了"在视觉上一眼可见 —— 收藏按钮的语义是
  //     "放进选材清单",多选的语义是"这次操作要处理这一篇",两者不能混;
  //   · 收藏按钮在多选态下隐藏,避免用户误以为点它是在"选中"。

  // T-Cron-2026-10-24 02:00 早间:触感反馈收口到 harness/haptics.js。
  // 之前本文件自己写了一份 tapHaptic,和 PostDetail / FavoriteBar / App 里
  // 的 buzz() 重复三份 —— 「尊重 prefers-reduced-motion」的判断必须四处一致,
  // 漏一处就意味着开了「减弱动态效果」的用户仍会收到震动。
  const tapHaptic = React.useCallback((ms = 8) => {
    buzz(ms);
  }, []);

  // 🔴 T-Cron-2026-11-01 14:00 — 修「版本状态不同步」(P1)。
  //
  // 【原缺陷(可复现)】
  //   这个 effect **只处理"有 preferredVersion"的方向**:
  //       if (preferredVersion && ...) setActiveVersion(preferredVersion);
  //   ⇒ 当用户「清除全部筛选」(filters.version 置回 '')时,
  //     effect 什么都不做 ⇒ 卡片**继续显示上一次的 B 版**,
  //     而筛选器里版本筛选已经被清空。
  //   ⇒ 筛选器说「无版本限制」、卡片说「B 版」⇒ 状态自相矛盾。
  //
  // 【更隐蔽的后果:两条复制路径口径不一致】
  //   · 单篇复制(详情抽屉)用卡片同名的 versionData ⇒ 拿到 B 版;
  //   · 批量复制走 buildBatchCopyText(list, filters.version)
  //     → resolveVersionKey 在 preferred 为空时**回落 A 版** ⇒ 拿到 A 版。
  //   ⇒ 同一个页面上「单篇复制」和「批量复制」文案不同,
  //     而且用户完全无从察觉(两个数字看着都合理)。
  //
  // 【修法:用共享的 resolveVersionKey 作为唯一口径】
  //   resolveVersionKey(post, preferred) 的规则:
  //     preferred 存在且该版本真实存在 → 用它;
  //     否则 → 'A';
  //     否则(没有 A)→ 第一个可用版本。
  //   ⇒ 把「无筛选」明确解析成 A 版,卡片 / 详情 / 批量复制三处
  //     用的是同一个函数 ⇒ 口径永远一致。
  //
  // 🔴 T-Cron-2026-11-02 08:00 早间 — 删掉这个 effect(T131 的 P0 收尾)。
  //
  // 【为什么必须删,而不是留着】
  //   它把 state 洗成 resolveVersionKey(post, preferredVersion),
  //   而 resolveVersionKey(null, ...) = '' ⇒ 卡片未挂载时 state 被洗成 ''
  //   ⇒ 用户点卡片那一帧 effect 还没跑 ⇒ activeVersion 仍是 ''
  //   ⇒ getVersionData 回退扁平 post(没有 .title)
  //   ⇒ versionData.title.length 抛 TypeError ⇒ ErrorBoundary ⇒ 白屏。
  //
  //   🔴 effect 晚于 render 一帧 ⇒ 用 effect 同步外部 prop 必然有陈旧窗口。
  //   ⇒ 版本改用**render 期推导**(见上方 userVersion / resolvedVersion / activeVersion),
  //     本 effect 必须删除,否则它会把 state 洗成无效键。
  //
  // 【原注释保留的教训仍然有效】
  //   依赖数组**不能**用 post 对象本身:收藏会让 store 重建 post 对象,
  //   若依赖 post,收藏一下就会把用户手动切到的 B 版打回 A 版。
  //   ⇒ render 期推导里用的是 post.id(业务身份)而不是 post(数据对象)。

  // Get version data safely
  const getVersionData = (post, ver) => {
    if (post.versions && post.versions[ver]) return post.versions[ver];
    // Fallback to flat model
    return post;
  };

  const versionData = getVersionData(post, activeVersion);
  const versionKeys = post.versions ? Object.keys(post.versions).sort() : [];
  const hasMultiVersions = versionKeys.length > 1;
  // Cover switches with the active version (per-version cover), fallback to post-level cover
  const coverUrl = (versionData && versionData.coverUrl) || post.coverUrl || '';

  // 🔴 T114 (2026-10-27):封面加载失败的降级。
  //
  // 为什么需要:CF Pages 对**不存在的静态文件**返回 200 + index.html
  // (SPA 单页应用回退)。浏览器拿到这段 HTML 去渲染 <img> ⇒
  // 触发 onerror ⇒ 卡片上出现一个**浏览器的碎图标 + alt 文字**,
  // 整个卡片看起来像"坏了",而不是"这张封面暂时没有"。
  //
  // 之前全站 0 处 onError ⇒ 封面缺失是**静默的视觉损坏**。
  // 现在降级成和"本来就没有封面"一样的排版占位块,
  // 文案保留(《书名》)+ 提示(封面暂不可用),用户能看懂发生了什么。
  const [coverBroken, setCoverBroken] = React.useState(false);

  // 换版本时重置失败态 —— 否则上一版的失败会把新版的封面也一起藏掉。
  React.useEffect(() => {
    setCoverBroken(false);
  }, [coverUrl]);

  // Short label for toggle: strip the suffix after "·" for brevity
  const versionToggleLabel = (ver) => {
    const full = post.versions[ver] && post.versions[ver].versionLabel;
    return full || ver;
  };

  const handleFavClick = (e) => {
    e.stopPropagation();
    tapHaptic(post.favorite ? 4 : 8); // 取消收藏时用更轻的震动
    onToggleFav(post.id);
  };

  // T-Cron-2026-10-24 02:00 早间 — 改进清单 #8「长按多选」。
  //
  // 长按卡片 → 进入多选态 + 选中这一张。
  // 关键约束:长按的那次长按**不能**顺带打开详情抽屉(否则用户会发现
  // 「长按 = 既进了多选、又弹出了详情」,完全没法用)。useLongPress 的
  // consumeLongPress() 负责吃掉长按后紧随的那次 click。
  const handleLongPress = React.useCallback(() => {
    if (!onToggleSelect) return;
    tapHaptic(12);
    onToggleSelect(post.id, true);
  }, [onToggleSelect, post.id, tapHaptic]);

  const longPress = useLongPress({ onLongPress: handleLongPress });

  // 多选态下的点击 = 切换选中;普通态 = 打开详情。
  const handleCardActivate = () => {
    if (selectionMode) {
      tapHaptic(selected ? 4 : 8);
      if (onToggleSelect) onToggleSelect(post.id, !selected);
      return;
    }
    tapHaptic(8);
    if (onClick) onClick(post.id);
  };

  const handleVersionChange = (e, ver) => {
    e.stopPropagation();
    tapHaptic(6);
    // 🔴 T-Cron-2026-11-02:写 userVersion(用户手动选择),不是 activeVersion。
    //   activeVersion 现在是 render 期推导出来的常量,不可写。
    setUserVersion(ver);
  };

  // ← / → 在卡片上直接循环版本(与根目录老仓库 PostCard 的键位契约一致),
  // 让键盘用户不用先打开详情就能翻 A/B/C。
  const cycleVersion = (delta) => {
    if (!versionKeys.length) return;
    const idx = versionKeys.indexOf(activeVersion);
    const next = versionKeys[(((idx + delta) % versionKeys.length) + versionKeys.length) % versionKeys.length];
    if (next && next !== activeVersion) {
      tapHaptic(6);
      setUserVersion(next);
    }
  };

  return (
    <div
      className={`post-card ${post.favorite ? 'favorited' : ''} ${selectionMode ? 'select-mode' : ''} ${selected ? 'selected' : ''}`}
      role="button"
      tabIndex={0}
      aria-pressed={selectionMode ? !!selected : undefined}
      aria-label={
        selectionMode
          ? `《${post.book || '未分类选题'}》${activeVersion} 版笔记${selected ? ',已选中' : ',未选中'},点击切换选中`
          : `《${post.book || '未分类选题'}》${activeVersion} 版笔记${post.favorite ? ',已收藏' : ''}`
      }
      onClick={(e) => {
        // 子按钮(收藏 / 版本切换)自己处理点击,不要连带触发打开详情
        if (e.target.closest('button')) return;
        // 长按后的那次 click 已经被长按消费掉了 —— 不能既进多选又打开详情。
        if (longPress.consumeLongPress()) return;
        handleCardActivate();
      }}
      onTouchStart={longPress.onTouchStart}
      onTouchMove={longPress.onTouchMove}
      onTouchEnd={longPress.onTouchEnd}
      onTouchCancel={longPress.onTouchCancel}
      // 移动端长按默认会弹出「保存图片 / 复制」的系统菜单,会直接打断
      // 多选流程 ⇒ 在多选态下屏蔽。长按封面保存封面是 PostDetail 的能力,
      // 卡片上的长按语义是"多选",两者必须分开。
      //
      // T-Cron-2026-10-24 03:30:这里不能只看 `selectionMode` —— React 的
      // 重渲染是异步的,而 Android 的长按菜单阈值(~500ms)和我们的长按
      // 定时器(450ms)赛跑:菜单可能在我们把 `selectionMode` 刷到 DOM
      // 之前就弹出来了。
      // ⇒ 额外查 `longPress.isFired()`(同步 ref,不看渲染时序),
      // 只要长按已经触发就一定吞掉这个菜单。
      onContextMenu={(e) => {
        if (selectionMode || longPress.isFired()) e.preventDefault();
      }}
      onKeyDown={(e) => {
        // 🔴 T137 (2026-11-03 08:00 早间) — 长按(auto-repeat)守卫。
        //
        // 【为什么这一层要自己挡】全局 handler 的守卫在 document 冒泡阶段,
        //   卡片是 React 合成事件(冒泡到 root)⇒ 卡片自己的 handler **先跑**
        //   ⇒ 全局守卫根本管不到卡片层的键。
        //
        // 【症状】按住 → 不放 ⇒ 版本 A→B→A 无限循环 + 每次 tapHaptic 震动;
        //   按住 Enter 不放 ⇒ 反复打开详情抽屉。
        //
        // 【Alt+↓ 侧栏重排】那是 Sidebar 的组合键,组合键 repeat 是期望行为
        //   ⇒ 放行组合键,只挡**单键**的 repeat。
        if (e.repeat && !(e.ctrlKey || e.metaKey || e.altKey)) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleCardActivate();
        } else if (e.key === 'ArrowLeft' && hasMultiVersions) {
          e.preventDefault();
          cycleVersion(-1);
        } else if (e.key === 'ArrowRight' && hasMultiVersions) {
          e.preventDefault();
          cycleVersion(1);
        } else if (hasMultiVersions && /^[1-9]$/.test(e.key)) {
          // 数字键直选版本(1 → 第一个版本,2 → 第二个……)。
          // 边界:只有真实存在的版本号才响应,避免越界声明。
          const idx = Number(e.key) - 1;
          if (idx < 0 || idx >= versionKeys.length) return;
          e.preventDefault();
          e.stopPropagation();
          if (versionKeys[idx] !== activeVersion) {
            tapHaptic(6);
            setUserVersion(versionKeys[idx]);
          }
        }
      }}
    >
      <div className="post-card-cover">
        {coverUrl && !coverBroken ? (
          <img
            key={`${post.id}-${activeVersion}`}
            src={coverUrl}
            alt={post.book}
            loading="lazy"
            decoding="async"
            // 🔴 T114:封面 404 / 回退 HTML 时降级成占位块,
            // 而不是留下浏览器的碎图标。详见上方 coverBroken 的说明。
            onError={() => setCoverBroken(true)}
          />
        ) : (
          <div className={`post-card-cover-placeholder${coverBroken ? ' is-broken' : ''}`}>
            {coverBroken ? (
              <>
                <span className="pc-cover-broken-icon" aria-hidden="true">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                    <rect x="3" y="3" width="18" height="18" rx="2"></rect>
                    <circle cx="8.5" cy="8.5" r="1.5"></circle>
                    <path d="M21 15l-5-5L5 21"></path>
                  </svg>
                </span>
                <span className="pc-cover-broken-text">封面暂不可用</span>
                <span className="pc-cover-broken-book">《{post.book}》</span>
              </>
            ) : (
              <>《{post.book}》</>
            )}
          </div>
        )}

        {/* Favorite stamp */}
        <div className="fav-stamp">已选</div>

        {/* Version indicator badge */}
        {hasMultiVersions && (
          <div className="version-badge source-ab">
            {activeVersion}版 · {versionData.versionLabel}
          </div>
        )}

        {/* Favorite button —— 多选态下隐藏,避免"点它 = 选中"的语义混淆 */}
        {!selectionMode && (
          <button
            className={`fav-btn ${post.favorite ? 'favorited' : ''}`}
            onClick={handleFavClick}
            aria-label={post.favorite ? '取消收藏' : '收藏'}
          >
            <HeartIcon filled={post.favorite} />
          </button>
        )}

        {/* T-Cron-2026-10-24 02:00 早间 — 改进清单 #8:多选勾选标记。
            放在收藏按钮原来的位置(右上角),选中时可见。 */}
        {selectionMode && (
          <div className={`select-check ${selected ? 'checked' : ''}`} aria-hidden="true">
            {selected ? (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            ) : null}
          </div>
        )}
      </div>

      <div className="post-card-body">
        <div className="post-card-book">
          <span>{post.book ? `《${post.book}》` : '未分类选题'}</span>
          <span className={`title-count-badge ${versionData.title.length > 19 ? 'over' : ''}`}>
            {versionData.title.length}/19
          </span>
        </div>

        <div className="post-card-title">{versionData.title}</div>

        {post.hotTopic && (
          <div className="post-card-hot-topic" title={post.hotTopic}>
            🔥 {post.hotTopic}
          </div>
        )}

        {/* Schedule row: publish date + recommended time (15类规则推导) */}
        {(post.publishDate || post.recommendTime) && (
          <div
            className="post-card-schedule"
            title={post.contentCategory ? `内容分类：${post.contentCategory}，按类别推荐时段发布` : '按类别推荐时段发布'}
          >
            <span className="pc-schedule-main">
              <svg className="pc-schedule-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="3" y="5" width="18" height="16" rx="2"></rect>
                <path d="M8 3v4"></path>
                <path d="M16 3v4"></path>
                <path d="M3 10h18"></path>
              </svg>
              {post.publishDate ? post.publishDate.slice(5) : '—'}
              <span className="pc-schedule-sep">·</span>
              推荐 {post.recommendTime || '—'} 发布
            </span>
            {post.contentCategory && (
              <span className="pc-schedule-cat">{post.contentCategory}</span>
            )}
          </div>
        )}

        <div className="post-card-summary">{versionData.summary}</div>

        {/* A/B/C Version Toggle - driven by available versions in data */}
        {hasMultiVersions && (
          <div className="version-toggle" onClick={(e) => e.stopPropagation()}>
            {versionKeys.map(ver => (
              <button
                key={ver}
                className={`version-toggle-btn ${activeVersion === ver ? 'active' : ''}`}
                onClick={(e) => handleVersionChange(e, ver)}
                /* T-Cron-2026-10-22 08:00 早间:版本按钮补 aria-label + aria-pressed。
                   之前按钮的可见文本是 "A · 热点主导" 这种复合串,读屏念出来是
                   一串碎片,用户听不出"当前选中的是哪一版"。aria-pressed 让
                   读屏明确播报"已按下/未按下",aria-label 给出干净的语义。 */
                aria-label={`切换到 ${ver} 版 ${versionToggleLabel(ver)}`}
                aria-pressed={activeVersion === ver}
                aria-keyshortcuts={String(versionKeys.indexOf(ver) + 1)}
              >
                <span className="version-toggle-label"><span className="vt-letter">{ver}</span><span className="vt-text"> · {versionToggleLabel(ver)}</span></span>
              </button>
            ))}
          </div>
        )}

        <div className="post-card-tags">
          {versionData.tags.slice(0, 3).map(tag => (
            <span key={tag} className="post-card-tag">#{tag}</span>
          ))}
        </div>

        <div className="post-card-meta">
          {post.needsReview && (
            <span className="post-card-meta-review" title="周一批次选题，发布前需人工复核">待人工复核</span>
          )}
          {post.batch && (
            <span className="post-card-meta-batch">{post.batch}批次</span>
          )}
          <span className="post-card-meta-angle">{versionData.angle}</span>
          <span>{versionData.wordCount}字</span>
        </div>
      </div>
    </div>
  );
}

window.PostCard = PostCard;

export default PostCard;
